import type { TFunction } from 'i18next';

import {
  type GraphQLLetter,
  GraphQLLetterFriendlyStatus,
} from '@sola/schema/dotcom/supporters/schema.generated.ts';

import { ActivityType } from '../recent-activity-item/recent-activity-item.logic.ts';
import { type RecentActivityItemProps } from '../recent-activity-item/recent-activity-item.tsx';

export enum LetterInStepsEnum {
  LetterSubmitted = 'recentActivity.letter.preparing',
  InTranslation = 'recentActivity.letter.inTranslation',
  Delivered = 'recentActivity.letter.delivered',
}

export type RecentActivityItemLetterInProps = {
  letter: GraphQLLetter;
};

const steps = Object.values(LetterInStepsEnum);

export const processLetterIn = (
  letter: GraphQLLetter,
  t: TFunction,
): RecentActivityItemProps => {
  let activeStep: LetterInStepsEnum | undefined;
  let successHeading: string | undefined;
  let errorHeading: string | undefined;
  let message: string | undefined;
  let estimatedDeliveryDate: string | undefined;
  let estimatedDeliveryTooltip: string | undefined;
  let ctaTitle: string | undefined;
  let ctaHref: string | undefined;

  switch (letter.status) {
    case GraphQLLetterFriendlyStatus.Complete: {
      activeStep = LetterInStepsEnum.Delivered;
      successHeading = letter.isReadBySupporter
        ? t('my:recentActivity.letter.delivered')
        : t('my:recentActivity.letter.new');
      if (letter.readyDate) {
        message = t('my:recentActivity.date', {
          date: letter.readyDate,
        });
      }
      ctaTitle = t('my:recentActivity.letter.read');
      ctaHref = `${t('my:ctas.writeLetter.url').replace(/\/$/, '')}/communications/${encodeURIComponent(letter.id)}`;

      break;
    }
    case GraphQLLetterFriendlyStatus.InTranslation:
    case GraphQLLetterFriendlyStatus.TranslationComplete:
    case GraphQLLetterFriendlyStatus.InQualityAssurance: {
      activeStep = LetterInStepsEnum.InTranslation;
      estimatedDeliveryDate = t('my:recentActivity.onItsWay');
      estimatedDeliveryTooltip = t('my:recentActivity.letter.tooltip');
      break;
    }
    case GraphQLLetterFriendlyStatus.Submitted:
    case GraphQLLetterFriendlyStatus.InProcess:
    case GraphQLLetterFriendlyStatus.JustReceived: {
      activeStep = LetterInStepsEnum.LetterSubmitted;
      estimatedDeliveryDate = t('my:recentActivity.onItsWay');
      estimatedDeliveryTooltip = t('my:recentActivity.letter.tooltip');
      break;
    }
    case GraphQLLetterFriendlyStatus.ReadyForDelivery: {
      activeStep = LetterInStepsEnum.Delivered;
      estimatedDeliveryDate = t('my:recentActivity.onItsWay');
      estimatedDeliveryTooltip = t('my:recentActivity.letter.tooltip');
      break;
    }
    default: {
      errorHeading = t('my:recentActivity.letter.errorIn');
      message = t('my:recentActivity.contactSupport');
      break;
    }
  }

  const cardTitle = t('my:recentActivity.cardTitleB2S', {
    type: 'Letter',
    name: letter.beneficiary?.name ?? '',
  });

  const activeStepIndex = activeStep ? steps.indexOf(activeStep) : -1;
  return {
    title: cardTitle,
    direction: ActivityType.Incoming,
    steps: steps.map((step) => t(`my:${step}`)),
    activeStepIndex: activeStepIndex,
    estimatedDelivery: estimatedDeliveryDate,
    estimatedDeliveryTooltip: estimatedDeliveryTooltip,
    message: message,
    successHeading: successHeading,
    errorHeading: errorHeading,
    ctaTitle: ctaTitle,
    ctaHref: ctaHref,
  };
};
